import React, { useEffect, useState, useRef } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { FormField } from '../../../shared/ui/FormField';
import { LockIcon, CreditCardIcon, MailIcon, KeyIcon, FileTextIcon, DollarSignIcon, LinkIcon, SmartphoneIcon, BellIcon, FileIcon, ChevronDownIcon, ChevronRightIcon, CreditCardIcon as SavedCardIcon, GlobeIcon, SearchIcon, XIcon, CheckIcon } from 'lucide-react';
import { MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface StandardPaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onAmountChange?: (amount: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onReferenceChange?: (reference: string) => void;
  onSecurityTypeChange?: (securityType: 'public' | 'password' | 'email' | 'sms' | 'document' | 'geo') => void;
}
export function StandardPayment({
  onExpiryDateChange,
  onAmountChange,
  onCurrencyChange,
  onReferenceChange,
  onSecurityTypeChange
}: StandardPaymentProps) {
  const {
    t
  } = useTranslation('payments');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(getTenant().currency.code);
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [storePaymentMethod, setStorePaymentMethod] = useState(false);
  const [selectedPaymentProviders, setSelectedPaymentProviders] = useState<string[]>([]);
  const [securityOption, setSecurityOption] = useState<'public' | 'password' | 'email' | 'sms' | 'document' | 'geo'>('public');
  const [securityEmail, setSecurityEmail] = useState('');
  const [securityPhone, setSecurityPhone] = useState('');
  const [securityPassword, setSecurityPassword] = useState('');
  const [documentType, setDocumentType] = useState('government-id');
  const [customDocumentName, setCustomDocumentName] = useState('');
  const [customDocumentDescription, setCustomDocumentDescription] = useState('');
  const [securitySectionExpanded, setSecuritySectionExpanded] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const countries = [{
    code: 'US',
    name: 'United States',
    flag: '🇺🇸'
  }, {
    code: 'CA',
    name: 'Canada',
    flag: '🇨🇦'
  }, {
    code: 'GB',
    name: 'United Kingdom',
    flag: '🇬🇧'
  }, {
    code: 'AU',
    name: 'Australia',
    flag: '🇦🇺'
  }, {
    code: 'DE',
    name: 'Germany',
    flag: '🇩🇪'
  }, {
    code: 'FR',
    name: 'France',
    flag: '🇫🇷'
  }, {
    code: 'JP',
    name: 'Japan',
    flag: '🇯🇵'
  }, {
    code: 'CN',
    name: 'China',
    flag: '🇨🇳'
  }, {
    code: 'IN',
    name: 'India',
    flag: '🇮🇳'
  }, {
    code: 'BR',
    name: 'Brazil',
    flag: '🇧🇷'
  }, {
    code: 'MX',
    name: 'Mexico',
    flag: '🇲🇽'
  }, {
    code: 'ES',
    name: 'Spain',
    flag: '🇪🇸'
  }, {
    code: 'IT',
    name: 'Italy',
    flag: '🇮🇹'
  }, {
    code: 'NL',
    name: 'Netherlands',
    flag: '🇳🇱'
  }, {
    code: 'SE',
    name: 'Sweden',
    flag: '🇸🇪'
  }, {
    code: 'NO',
    name: 'Norway',
    flag: '🇳🇴'
  }, {
    code: 'DK',
    name: 'Denmark',
    flag: '🇩🇰'
  }, {
    code: 'FI',
    name: 'Finland',
    flag: '🇫🇮'
  }, {
    code: 'SG',
    name: 'Singapore',
    flag: '🇸🇬'
  }, {
    code: 'AE',
    name: 'United Arab Emirates',
    flag: '🇦🇪'
  }, {
    code: 'AR',
    name: 'Argentina',
    flag: '🇦🇷'
  }, {
    code: 'AT',
    name: 'Austria',
    flag: '🇦🇹'
  }, {
    code: 'BE',
    name: 'Belgium',
    flag: '🇧🇪'
  }, {
    code: 'CL',
    name: 'Chile',
    flag: '🇨🇱'
  }, {
    code: 'CO',
    name: 'Colombia',
    flag: '🇨🇴'
  }, {
    code: 'CZ',
    name: 'Czech Republic',
    flag: '🇨🇿'
  }, {
    code: 'EG',
    name: 'Egypt',
    flag: '🇪🇬'
  }, {
    code: 'GR',
    name: 'Greece',
    flag: '🇬🇷'
  }, {
    code: 'HK',
    name: 'Hong Kong',
    flag: '🇭🇰'
  }, {
    code: 'HU',
    name: 'Hungary',
    flag: '🇭🇺'
  }, {
    code: 'ID',
    name: 'Indonesia',
    flag: '🇮🇩'
  }, {
    code: 'IE',
    name: 'Ireland',
    flag: '🇮🇪'
  }, {
    code: 'IL',
    name: 'Israel',
    flag: '🇮🇱'
  }, {
    code: 'KR',
    name: 'South Korea',
    flag: '🇰🇷'
  }, {
    code: 'MY',
    name: 'Malaysia',
    flag: '🇲🇾'
  }, {
    code: 'NG',
    name: 'Nigeria',
    flag: '🇳🇬'
  }, {
    code: 'NZ',
    name: 'New Zealand',
    flag: '🇳🇿'
  }, {
    code: 'PH',
    name: 'Philippines',
    flag: '🇵🇭'
  }, {
    code: 'PL',
    name: 'Poland',
    flag: '🇵🇱'
  }, {
    code: 'PT',
    name: 'Portugal',
    flag: '🇵🇹'
  }, {
    code: 'RO',
    name: 'Romania',
    flag: '🇷🇴'
  }, {
    code: 'RU',
    name: 'Russia',
    flag: '🇷🇺'
  }, {
    code: 'SA',
    name: 'Saudi Arabia',
    flag: '🇸🇦'
  }, {
    code: 'CH',
    name: 'Switzerland',
    flag: '🇨🇭'
  }, {
    code: 'TH',
    name: 'Thailand',
    flag: '🇹🇭'
  }, {
    code: 'TR',
    name: 'Turkey',
    flag: '🇹🇷'
  }, {
    code: 'TW',
    name: 'Taiwan',
    flag: '🇹🇼'
  }, {
    code: 'UA',
    name: 'Ukraine',
    flag: '🇺🇦'
  }, {
    code: 'VN',
    name: 'Vietnam',
    flag: '🇻🇳'
  }, {
    code: 'ZA',
    name: 'South Africa',
    flag: '🇿🇦'
  }];
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const filteredCountries = countrySearchQuery ? countries.filter(c => c.name.toLowerCase().includes(countrySearchQuery.toLowerCase()) || c.code.toLowerCase().includes(countrySearchQuery.toLowerCase())) : countries;
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
    onExpiryDateChange?.(date);
  };
  const handleAmountChange = (v: string) => {
    setAmount(v);
    onAmountChange?.(v);
  };
  const handleCurrencyChange = (v: string) => {
    setCurrency(v);
    onCurrencyChange?.(v);
  };
  const handleReferenceChange = (v: string) => {
    setReference(v);
    onReferenceChange?.(v);
  };
  const handleSecurityOptionChange = (v: 'public' | 'password' | 'email' | 'sms' | 'document' | 'geo') => {
    setSecurityOption(v);
    onSecurityTypeChange?.(v);
  };
  const toggleCountrySelection = (code: string) => {
    setSelectedCountries(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };
  const removeSelectedCountry = (code: string) => {
    setSelectedCountries(prev => prev.filter(c => c !== code));
  };
  const getCountryByCode = (code: string) => countries.find(c => c.code === code);
  // Shared input class
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
  // Security option summary when collapsed
  const securitySummary = () => {
    if (securityOption === 'public') return null;
    const labels: Record<string, string> = {
      password: 'Password protected',
      email: 'Email verification',
      sms: 'SMS verification',
      document: 'Document verification',
      geo: `Geo-restricted (${selectedCountries.length} ${selectedCountries.length === 1 ? 'country' : 'countries'})`
    };
    return labels[securityOption] || null;
  };
  return <div className="space-y-5" data-id="element-8791">
      {/* ─── PAYMENT DETAILS ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8792">
        <div className="p-6 space-y-5" data-id="element-8793">
          {/* Amount + Currency — side by side */}
          <div data-id="element-8794">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8795">
              {t('components.BulkPaymentPreview.amount')}{' '}
              <span className="text-red-400" data-id="element-8796">*</span>
            </label>
            <div className="relative" data-id="element-8797">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8798">
                <span className="text-sm font-medium text-gray-500" data-id="element-8799">
                  {t('components.BulkPaymentListSimplified.usd')}
                </span>
              </div>
              <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={amount} onChange={e => handleAmountChange(e.target.value)} data-id="element-8800" />
            </div>
          </div>

          {/* Reference + Description — 2-column grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-8801">
            <div data-id="element-8802">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8803">
                {t('components.BulkPaymentPreview.reference')}
              </label>
              <input type="text" className={inputClass} placeholder={t('components.StandardPayment.attrs.eg_inv1234')} value={reference} onChange={e => handleReferenceChange(e.target.value)} data-id="element-8804" />
            </div>
            <div data-id="element-8805">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8806">
                {t('components.BulkPaymentPreview.description')}
              </label>
              <input type="text" className={inputClass} placeholder={t('components.StandardPayment.attrs.eg_monthly_subscription')} value={description} onChange={e => setDescription(e.target.value)} data-id="element-8807" />
            </div>
          </div>
        </div>
      </div>

      {/* ─── SECURITY SETTINGS ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8808">
        <button className="w-full text-left flex items-center justify-between px-6 py-4" onClick={() => setSecuritySectionExpanded(!securitySectionExpanded)} data-id="element-8809">
          <div className="flex items-center gap-2" data-id="element-8810">
            <LockIcon size={16} className="text-[#9F140F]" data-id="element-8811" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-8812">
              {t('components.StandardPayment.security_settings')}
            </span>
            {!securitySectionExpanded && securitySummary() && <span className="text-xs text-gray-500 ml-2 font-normal" data-id="element-8813">
                — {securitySummary()}
              </span>}
          </div>
          {securitySectionExpanded ? <ChevronDownIcon size={16} className="text-gray-400" data-id="element-8814" /> : <ChevronRightIcon size={16} className="text-gray-400" data-id="element-8815" />}
        </button>

        {securitySectionExpanded && <div className="px-6 pb-6" data-id="element-8816">
            <p className="text-xs text-gray-500 mb-4" data-id="element-8817">
              {t('components.StandardPayment.control_how_customers_access_this_payment')}
            </p>

            {/* 2-column grid for compact security options */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-8818">
              {/* Public */}
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${securityOption === 'public' ? 'border-[#E2231A] bg-[#E2231A]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-8819">
                <input type="radio" name="securityOption" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300" checked={securityOption === 'public'} onChange={() => handleSecurityOptionChange('public')} data-id="element-8820" />

                <div className="min-w-0" data-id="element-8821">
                  <div className="flex items-center gap-1.5" data-id="element-8822">
                    <LinkIcon size={13} className="text-gray-400 flex-shrink-0" data-id="element-8823" />

                    <span className="text-sm font-medium text-gray-900" data-id="element-8824">
                      {t('components.StandardPayment.public_access')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-8825">
                    {t('components.StandardPayment.anyone_with_the_link_can_pay')}
                  </p>
                </div>
              </label>

              {/* Password */}
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${securityOption === 'password' ? 'border-[#E2231A] bg-[#E2231A]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-8826">
                <input type="radio" name="securityOption" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300" checked={securityOption === 'password'} onChange={() => handleSecurityOptionChange('password')} data-id="element-8827" />

                <div className="min-w-0" data-id="element-8828">
                  <div className="flex items-center gap-1.5" data-id="element-8829">
                    <KeyIcon size={13} className="text-gray-400 flex-shrink-0" data-id="element-8830" />

                    <span className="text-sm font-medium text-gray-900" data-id="element-8831">
                      {t('components.StandardPayment.password')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-8832">
                    {t('components.StandardPayment.require_a_password_to_access')}
                  </p>
                </div>
              </label>

              {/* Email */}
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${securityOption === 'email' ? 'border-[#E2231A] bg-[#E2231A]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-8833">
                <input type="radio" name="securityOption" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300" checked={securityOption === 'email'} onChange={() => handleSecurityOptionChange('email')} data-id="element-8834" />

                <div className="min-w-0" data-id="element-8835">
                  <div className="flex items-center gap-1.5" data-id="element-8836">
                    <MailIcon size={13} className="text-gray-400 flex-shrink-0" data-id="element-8837" />

                    <span className="text-sm font-medium text-gray-900" data-id="element-8838">
                      {t('components.StandardPayment.email_verification')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-8839">
                    {t('components.StandardPayment.verify_via_email_code')}
                  </p>
                </div>
              </label>

              {/* SMS */}
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${securityOption === 'sms' ? 'border-[#E2231A] bg-[#E2231A]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-8840">
                <input type="radio" name="securityOption" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300" checked={securityOption === 'sms'} onChange={() => handleSecurityOptionChange('sms')} data-id="element-8841" />

                <div className="min-w-0" data-id="element-8842">
                  <div className="flex items-center gap-1.5" data-id="element-8843">
                    <SmartphoneIcon size={13} className="text-gray-400 flex-shrink-0" data-id="element-8844" />

                    <span className="text-sm font-medium text-gray-900" data-id="element-8845">
                      {t('components.StandardPayment.sms_verification')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-8846">
                    {t('components.StandardPayment.verify_via_phone_number')}
                  </p>
                </div>
              </label>

              {/* Geo */}
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${securityOption === 'geo' ? 'border-[#E2231A] bg-[#E2231A]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-8847">
                <input type="radio" name="securityOption" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300" checked={securityOption === 'geo'} onChange={() => handleSecurityOptionChange('geo')} data-id="element-8848" />

                <div className="min-w-0" data-id="element-8849">
                  <div className="flex items-center gap-1.5" data-id="element-8850">
                    <GlobeIcon size={13} className="text-gray-400 flex-shrink-0" data-id="element-8851" />

                    <span className="text-sm font-medium text-gray-900" data-id="element-8852">
                      {t('components.StandardPayment.geographic_restriction')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-8853">
                    {t('components.StandardPayment.restrict_by_country')}
                  </p>
                </div>
              </label>

              {/* Document */}
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${securityOption === 'document' ? 'border-[#E2231A] bg-[#E2231A]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-8854">
                <input type="radio" name="securityOption" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300" checked={securityOption === 'document'} onChange={() => handleSecurityOptionChange('document')} data-id="element-8855" />

                <div className="min-w-0" data-id="element-8856">
                  <div className="flex items-center gap-1.5" data-id="element-8857">
                    <FileTextIcon size={13} className="text-gray-400 flex-shrink-0" data-id="element-8858" />

                    <span className="text-sm font-medium text-gray-900" data-id="element-8859">
                      {t('components.StandardPayment.document_upload')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-8860">
                    {t('components.StandardPayment.require_document_before_payment')}
                  </p>
                </div>
              </label>
            </div>

            {/* Expanded detail for selected security option */}
            {securityOption === 'password' && <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-8861">
                <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-8862">
                  {t('components.StandardPayment.password')}
                </label>
                <input type="password" className={inputClass} placeholder={t('components.StandardPayment.attrs.enter_a_secure_password')} value={securityPassword} onChange={e => setSecurityPassword(e.target.value)} data-id="element-8863" />
              </div>}

            {securityOption === 'email' && <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-8864">
                <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-8865">
                  {t('components.StandardPayment.customer_email')}
                </label>
                <input type="email" className={inputClass} placeholder={t('components.RecurringPayment.attrs.customerexamplecom')} value={securityEmail} onChange={e => setSecurityEmail(e.target.value)} data-id="element-8866" />
              </div>}

            {securityOption === 'sms' && <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-8867">
                <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-8868">
                  {t('components.StandardPayment.customer_phone')}
                </label>
                <input type="tel" className={inputClass} placeholder="+1 (555) 000-0000" value={securityPhone} onChange={e => setSecurityPhone(e.target.value)} data-id="element-8869" />
              </div>}

            {securityOption === 'geo' && <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-8870">
                <label className="block text-xs font-medium text-gray-600 mb-2" data-id="element-8871">
                  {t('components.StandardPayment.allowed_countries')}
                </label>

                {selectedCountries.length > 0 && <div className="flex flex-wrap gap-1.5 mb-3" data-id="element-8872">
                    {selectedCountries.map(code => {
              const country = getCountryByCode(code);
              return <span key={code} className="inline-flex items-center gap-1 bg-[#FEE7E5] text-[#9F140F] rounded-full px-2.5 py-1 text-xs" data-id="element-8873">
                          {country?.flag} {country?.name}
                          <button onClick={e => {
                  e.preventDefault();
                  removeSelectedCountry(code);
                }} aria-label={`Remove ${country?.name}`} className="text-[#9F140F] hover:text-[#8C6C00]" data-id="element-8874">
                            <XIcon size={12} aria-hidden="true" data-id="element-8875" />
                          </button>
                        </span>;
            })}
                  </div>}

                <div className="relative" ref={countryDropdownRef} data-id="element-8876">
                  <div className="relative" data-id="element-8877">
                    <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-8878" />

                    <input type="text" className={`${inputClass} pl-9`} placeholder={t('components.StandardPayment.attrs.search_countries')} value={countrySearchQuery} onChange={e => setCountrySearchQuery(e.target.value)} onFocus={() => setShowCountryDropdown(true)} data-id="element-8879" />
                  </div>
                  {showCountryDropdown && <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto" data-id="element-8880">
                      {filteredCountries.length > 0 ? filteredCountries.map(country => <div key={country.code} className={`px-3 py-2 hover:bg-gray-50 cursor-pointer flex justify-between items-center text-sm ${selectedCountries.includes(country.code) ? 'bg-[#FEE7E5]' : ''}`} onClick={() => toggleCountrySelection(country.code)} data-id="element-8881">
                            <span data-id="element-8882">
                              {country.flag} {country.name}
                            </span>
                            {selectedCountries.includes(country.code) && <CheckIcon size={14} className="text-[#9F140F]" data-id="element-8883" />}
                          </div>) : <div className="px-3 py-2 text-gray-500 text-sm" data-id="element-8884">
                          {t('components.StandardPayment.no_countries_found')}
                        </div>}
                    </div>}
                </div>

                {selectedCountries.length > 0 && <div className="flex justify-end mt-2" data-id="element-8885">
                    <button type="button" className="text-xs text-gray-500 hover:text-gray-700" onClick={() => setSelectedCountries([])} data-id="element-8886">
                      {t('components.StandardPayment.clear_all')}
                    </button>
                  </div>}
              </div>}

            {securityOption === 'document' && <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100 space-y-3" data-id="element-8887">
                <div className="flex items-start p-3 bg-amber-50 border border-amber-100 rounded-lg" data-id="element-8888">
                  <BellIcon size={13} className="text-amber-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-8889" />

                  <p className="text-xs text-amber-700" data-id="element-8890">
                    {t('components.StandardPayment.youll_be_notified_when_a_document')}
                  </p>
                </div>

                <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-8891">
                  {t('components.StandardPayment.required_document_type')}
                </label>
                <div className="space-y-2" data-id="element-8892">
                  {[{
              id: 'government-id',
              label: "Government-issued ID (Passport, Driver's License)"
            }, {
              id: 'proof-address',
              label: 'Proof of address (Utility bill, Bank statement)'
            }, {
              id: 'business-verification',
              label: 'Business verification (License, Tax ID)'
            }, {
              id: 'custom',
              label: 'Custom document'
            }].map(doc => <label key={doc.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-id="element-8893">
                      <input type="radio" name="documentType" className="h-3.5 w-3.5 text-[#E2231A] focus:ring-[#E2231A] border-gray-300" checked={documentType === doc.id} onChange={() => setDocumentType(doc.id)} data-id="element-8894" />

                      <span className="text-xs" data-id="element-8895">{doc.label}</span>
                    </label>)}
                </div>

                {documentType === 'custom' && <div className="mt-3 pl-5 space-y-3 border-l-2 border-gray-200" data-id="element-8896">
                    <div data-id="element-8897">
                      <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-8898">
                        {t('components.StandardPayment.document_name')}
                      </label>
                      <input type="text" className={inputClass} placeholder={t('components.StandardPayment.attrs.eg_proof_of_insurance')} value={customDocumentName} onChange={e => setCustomDocumentName(e.target.value)} data-id="element-8899" />
                    </div>
                    <div data-id="element-8900">
                      <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-8901">
                        {t('components.StandardPayment.instructions_for_customer')}
                      </label>
                      <textarea className={`${inputClass} resize-none`} placeholder={t('components.StandardPayment.attrs.explain_what_document_you_need')} rows={2} value={customDocumentDescription} onChange={e => setCustomDocumentDescription(e.target.value)} data-id="element-8902" />
                    </div>
                    <p className="text-[11px] text-gray-400 flex items-center" data-id="element-8903">
                      <FileIcon size={11} className="mr-1" data-id="element-8904" />
                      {t('components.StandardPayment.shown_to_the_customer_on_the')}
                    </p>
                  </div>}
              </div>}
          </div>}
      </div>

      {/* ─── ADDITIONAL OPTIONS ─── */}
      <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} selectedPaymentProviders={selectedPaymentProviders} onSelectedPaymentProvidersChange={setSelectedPaymentProviders} storePaymentMethod={storePaymentMethod} onStorePaymentMethodChange={setStorePaymentMethod} data-id="element-8905" />
    </div>;
}